<template>
  <div class="video-preview__embed">
    <img
      v-if="showLoomThumbnail"
      :src="loomThumbnailUrl"
      alt="Video preview"
      class="video-preview__loom-thumbnail"
    />
    <iframe
      v-else-if="showLoomIframe"
      :src="embedUrl"
      title="Video preview"
      class="video-preview__iframe"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      allowfullscreen
    />
    <div
      v-else-if="showBrokenPreview"
      class="video-preview__broken"
    >
      <icon
        data="@icon/play-not-found.svg"
        width="var(--icon-24)"
        height="var(--icon-24)"
        color="var(--monochrome05to04)"
      />
      <span class="video-preview__broken-text">Broken video link</span>
    </div>
    <div
      v-else-if="isLoom"
      class="video-preview__loom-thumbnail"
      aria-hidden="true"
    />
    <div
      v-else
      :id="videoId"
      :data-plyr-provider="provider"
      :data-plyr-embed-id="embedId"
    />
    <div
      v-if="showOverlay && !showBrokenPreview"
      class="video-preview__overlay"
      @click.prevent="showVideo"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop } from "vue-property-decorator"
import Plyr from "plyr"

import "plyr/dist/plyr.css"

import {
  getLoomThumbnailUrl,
  getVideoEmbedUrl,
  isValidParsedVideo,
  parseVideoUrl
} from "@/helpers/parseVideoUrl"

@Options({
  name: "VideoEmbedPreview"
})
export default class extends Vue {
  @Prop() readonly videoUrl
  @Prop({ default: "" }) readonly playerId
  @Prop({ default: true }) readonly showOverlay: boolean
  @Prop({ default: false }) readonly showControls: boolean
  @Prop({ default: false }) readonly autoplay: boolean

  provider = ""
  embedId = ""
  loomThumbnailUrl = ""

  player: Plyr | null = null

  get isLoom() {
    return this.provider === "loom"
  }

  get embedUrl() {
    return this.isLoom ? getVideoEmbedUrl(this.videoUrl) : ""
  }

  get showLoomThumbnail() {
    return this.isLoom && this.showOverlay && !!this.loomThumbnailUrl
  }

  get showLoomIframe() {
    return this.isLoom && !this.showOverlay && !!this.embedUrl
  }

  get videoId() {
    return `player${this.playerId}`
  }

  get canRenderPlayer() {
    return isValidParsedVideo({
      provider: this.provider as "youtube" | "vimeo" | "loom" | "",
      embedId: this.embedId
    })
  }

  get showBrokenPreview() {
    return !!this.provider && !this.canRenderPlayer
  }

  mounted() {
    this.parseVideoUrl(this.videoUrl)

    this.$nextTick(() => {
      if (this.showBrokenPreview) {
        return
      }

      if (this.isLoom) {
        void this.loadLoomThumbnail()
        return
      }

      const player = new Plyr(`#${this.videoId}`, {
        controls: this.showControls
          ? ["play-large", "play", "progress", "current-time", "mute", "volume", "fullscreen"]
          : [],
        autoplay: this.autoplay
      })

      this.player = player
    })
  }

  parseVideoUrl(url) {
    const result = parseVideoUrl(url)

    this.provider = result.provider
    this.embedId = result.embedId
  }

  async loadLoomThumbnail() {
    this.loomThumbnailUrl = await getLoomThumbnailUrl(this.videoUrl)
  }

  @Emit()
  showVideo() {
    return {
      provider: this.provider,
      embedId: this.embedId
    }
  }

  beforeUnmount() {
    if (this.player) {
      this.player.destroy()
    }
  }
}
</script>
